import { Component, h } from "preact";
import { IReactConfiguration, ITemplate } from "../typings";
import Loader from "./modules/Loader";
import { IStorageController } from "../shared/controller/storage/istorage-controller";
import SessionStorageController from "../shared/controller/storage/session-storage-controller";
import MemoryStorageController from "../shared/controller/storage/memory-storage-controller";
import EventController from "../shared/controller/event-controller";
import TemplateController from "../shared/controller/template-controller";
import OutputController from "../shared/controller/output-controller";
import ExternalController from "../shared/controller/external-controller";

import Message from "./modules/Message";
import UploadYourLabel from ".";
import LabelUploader from "./label-uploader";
import UnitSystemController from "../shared/controller/unit-system-controller";
import SizeController from "../shared/controller/size-controller";
import MappingController from "../shared/controller/mapping-controller";
import ColorController from "../shared/controller/color-controller";

export default class Uploader extends Component<
  IUploaderProps,
  IUploaderState
> {
  uploader: LabelUploader;

  eventController: EventController;
  externalController: ExternalController;
  outputController: OutputController;
  sizeController: SizeController;
  storageController: IStorageController;
  templateController: TemplateController;
  unitSystemController: UnitSystemController;
  private mappingController: MappingController;
  private colorController: ColorController;
  conf: IReactConfiguration;
  private messageTimeout: any;

  constructor(props: IUploaderProps) {
    super(props);

    this.state = {
      templateLoaded: false,
      stateLoader: false,
      message: null,
    };

    this.conf = props.conf;

    this.bindLabelUploader = this.bindLabelUploader.bind(this);
  }

  componentDidMount(): void {
    this.eventController = new EventController(
      this.props.conf,
      this.props.outsideClass,
    );
    this.externalController = new ExternalController(
      this,
      this.eventController,
    );

    if (this.props.conf.canvas_only === true || this.props.conf.design) {
      this.storageController = new MemoryStorageController();
    } else {
      this.storageController = new SessionStorageController(this.props.conf);
    }
    if (this.props.conf.design) {
      this.storageController.saveOutputTemplate(this.props.conf.design);
      this.storageController.saveOutputTemplateAsync();
    }

    this.templateController = new TemplateController(this.storageController);

    this.templateController
      .downloadTemplate(
        this.props.conf.asset_prefix + this.props.conf.template_url,
      )
      .then((template: ITemplate) => {
        template = this.templateController.parseTemplate(template);
        this.templateController.saveTemplate(template);

        this.outputController = new OutputController(
          this.eventController,
          this.templateController,
          this.storageController,
          this.externalController,
          this.props.conf,
        );

        if (!this.storageController.getOutputTemplate()) {
          this.outputController.saveOutputTemplate(Object.assign({}, template));
        }

        this.unitSystemController = new UnitSystemController(
          this.props.conf,
          this.outputController,
        );
        this.sizeController = new SizeController(
          this.outputController,
          this.templateController,
          {
            croppedData: ["image", "cropped-data"],
            edges: ["label", "edges"],
            edge: ["label", "edge"],
            edgesIncludes: ["label", "edges_includes"],
            height: ["size", "height"],
            width: ["size", "width"],
          },
          this.unitSystemController,
        );
        this.sizeController.setChild("original", this.sizeController);
        this.sizeController.setChild(
          "original-back",
          new SizeController(
            this.outputController,
            this.templateController,
            {
              croppedData: ["image", "cropped-data-back"],
              edges: ["label", "edges"],
              edge: ["label", "edge"],
              edgesIncludes: ["label", "edges_includes"],
              height: ["size", "height"],
              width: ["size", "width"],
            },
            this.unitSystemController,
          ),
        );

        this.colorController = new ColorController(
          this.templateController,
          this.eventController,
          this.outputController,
          this.props.conf,
        );

        this.mappingController = new MappingController(
          this.outputController,
          this.templateController,
          this.props.conf,
        );

        if (this.storageController.hasOutputTemplate() === true) {
          this.outputController
            .importOutputTemplate(this.storageController.getOutputTemplate())
            .then(() => {
              if (!this.storageController.hasTemplate()) {
                this.getTemplate(
                  this.props.conf.asset_prefix + this.props.conf.template_url,
                );
              } else {
                this.outputController.processDefaults();
                this.eventController.sendLoadedEvent(this.externalController);
                this.setState(
                  {
                    templateLoaded: true,
                  },
                  () => {
                    this.mappingController.initEventListeners();
                    this.mappingController.mapLabelTypeToValues();
                  },
                );
              }
            });
        } else if (!this.storageController.hasTemplate()) {
          this.getTemplate(
            this.props.conf.asset_prefix + this.props.conf.template_url,
          );
        } else {
          this.outputController.processDefaults();
          this.eventController.sendLoadedEvent(this.externalController);
          this.setState(
            {
              templateLoaded: true,
            },
            () => {
              this.mappingController.initEventListeners();
              this.mappingController.mapLabelTypeToValues();
            },
          );
        }
      });
  }

  getTemplate(url: string): void {
    this.templateController
      .downloadTemplate(url)
      .then((template: ITemplate) => {
        template = this.templateController.parseTemplate(template);
        this.templateController.saveTemplate(template);

        if (!this.storageController.getOutputTemplate()) {
          this.outputController.saveOutputTemplate(Object.assign({}, template));
        }
        this.outputController.processDefaults();

        if (this.props.conf.decimalCharacter) {
          this.unitSystemController.saveDecimal(
            this.props.conf.decimalCharacter,
          );
        }
        this.eventController.sendLoadedEvent(this.externalController);
        this.mappingController.initEventListeners();
        this.mappingController.mapLabelTypeToValues();

        this.setState({
          templateLoaded: true,
        });
      })
      .catch((err: string) => {
        this.setMessage(err.toString());
      });
  }

  setMessage(message: string): void {
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
      this.messageTimeout = undefined;
    }
    this.setState(
      {
        message,
      },
      () =>
        (this.messageTimeout = setTimeout(() => this.clearMessage(), 30000)),
    );
  }

  clearMessage(): void {
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
      this.messageTimeout = undefined;
    }
    this.setState({
      message: null,
    });
  }

  bindLabelUploader(uploader: LabelUploader): void {
    this.uploader = uploader;
  }

  render(): JSX.Element {
    return (
      <div className="uploader-wrapper">
        <Message message={this.state.message} conf={this.props.conf} />
        <LabelUploader
          conf={this.props.conf}
          templateLoaded={this.state.templateLoaded}
          main={this}
          eventController={this.eventController}
          outputController={this.outputController}
          sizeController={this.sizeController}
          templateController={this.templateController}
          unitSystemController={this.unitSystemController}
          bind={this.bindLabelUploader}
          colorController={this.colorController}
        />
        <Loader state={this.state.stateLoader} />
      </div>
    );
  }
}

interface IUploaderProps {
  conf: IReactConfiguration;
  outsideClass?: UploadYourLabel;
}

interface IUploaderState {
  templateLoaded: boolean;
  stateLoader: boolean;
  message: string;
}
